import React from "react";
import moment from "moment";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsSelect from "../clients/Tools/kit/ToolsSelect";
import ToolsCheckbox from "../clients/Tools/kit/ToolsCheckbox";
import PlaybookEditService from "../../services/PlaybookEditService";
import UtilityService from "../../services/UtilityService";

import "./playbook-settings-panel.css";

const SCOPE_CHOICES = [
	{ value: "high_medium_and_journals", label: "High and medium findings, and every journal" },
	{ value: "everything", label: "Every finding, low ones too" },
];

const WHO_DECIDES_CHOICES = [
	{ value: PlaybookEditService.WHO_DECIDES.CLIENT_MANAGERS, label: "The client's own managers" },
	{ value: PlaybookEditService.WHO_DECIDES.ROLES, label: "Team members in roles I choose" },
];

const DAY_CHOICES =Array.from({ length: 28 }, (unused, index) => ({ value: String(index + 1), label: `Day ${index + 1} of the month` }));

class PlaybookSettingsPanel extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			panel: null,
			draft: null,
			changeNote: "",
			showUnheldRoles: false,
			isLoading: true,
			loadError: null,
			isSaving: false,
			saveError: null,
			savedAt: null,
		};
	}

	componentDidMount() {
		this.fetchPanel();
	}

	fetchPanel = async () => {
		const { playbookId } = this.props;
		try {
			const panel = await PlaybookEditService.getSettings({ playbookId });
			await this.update({ panel, draft: { ...panel.settings }, isLoading: false, loadError: null });
		} catch (error) {
			await this.update({ isLoading: false, loadError: UtilityService.describeRequestError({ error, fallback: "The settings could not be loaded." }) });
		}
	};

	toggleId = ({ listName, id }) => {
		const list = this.state.draft[listName] || [];
		const nextList = UtilityService.toggleInList({ list, item: id });
		this.setDraft({ [listName]: nextList });
	};

	setDraft = changes => {
		this.update({ draft: { ...this.state.draft, ...changes }, savedAt: null });
	};

	onSave = async () => {
		const { playbookId } = this.props;
		const { draft, changeNote } = this.state;
		await this.update({ isSaving: true, saveError: null });
		try {
			await PlaybookEditService.saveSettings({ playbookId, settings: draft, changeNote: changeNote.trim() });
			const panel = await PlaybookEditService.getSettings({ playbookId });
			await this.update({ panel, draft: { ...panel.settings }, changeNote: "", isSaving: false, savedAt: new Date() });
		} catch (error) {
			await this.update({ isSaving: false, saveError: UtilityService.describeRequestError({ error, fallback: "The settings did not save." }) });
		}
	};

	renderPills({ items, selectedIds, listName, disabled }) {
		const { showUnheldRoles } = this.state;
		const isUnheldRole = item => item.holderCount === 0 && !selectedIds.includes(item.id);
		const unheldCount = items.filter(isUnheldRole).length;
		const shownItems = showUnheldRoles ? items : items.filter(item => !isUnheldRole(item));

		return (
			<div className="agent-settings__pills">
				{shownItems.map(item => {
					const isOn = selectedIds.includes(item.id);
					return (
						<button
							key={item.id}
							type="button"
							className={clsx("agent-settings__pill", isOn && "agent-settings__pill--on")}
							onClick={() => this.toggleId({ listName, id: item.id })}
							disabled={disabled}
							aria-pressed={isOn}
						>
							{isOn && <Icon.Check size={12} />}
							{item.name}
							{item.holderCount > 0 && <span className="agent-settings__pill-count">{item.holderCount}</span>}
						</button>
					);
				})}
				{unheldCount > 0 && (
					<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => this.update({ showUnheldRoles: !showUnheldRoles })}>
						{showUnheldRoles ? "Hide roles nobody holds" : `Show ${unheldCount} roles nobody holds`}
					</button>
				)}
			</div>
		);
	}

	renderCoverage() {
		const { panel } = this.state;
		const isDecidedByClientManagers = panel.settings.whoDecides === PlaybookEditService.WHO_DECIDES.CLIENT_MANAGERS;
		const relevantTeams = panel.coverage.filter(team => team.quickBooksClients > 0 || team.deciders.length > 0);
		const teamsToShow = relevantTeams.length > 0 ? relevantTeams : panel.coverage;
		const coverageNote = isDecidedByClientManagers
			? "From the saved settings and each client's profile. A client that names nobody waits until someone is set on its profile."
			: "From the saved settings. Teams with QuickBooks clients or people in these roles are shown.";
		return (
			<section className="agent-settings__section">
				<div className="agent-settings__section-head">
					<span className="tk-text-strong">Who that is on each team</span>
					<span className="tk-muted">{coverageNote}</span>
				</div>
				<div className="agent-settings__coverage tk-card">
					<div className="agent-settings__coverage-row agent-settings__coverage-row--head">
						<span>Team</span>
						<span>QuickBooks clients</span>
						<span>Decides</span>
						<span>Signs off</span>
					</div>
					{teamsToShow.map(team => (
						<div key={team.teamId} className="agent-settings__coverage-row">
							<span className="agent-settings__team">{team.teamName}</span>
							<span className="agent-settings__number">{team.quickBooksClients}</span>
							<span className={clsx(team.hasNoDecider && "agent-settings__gap")}>
								{PlaybookEditService.describeCoverageCell({ names: team.deciders, clientsWithNobody: team.clientsWithoutDecider, isRequired: true })}
							</span>
							<span className={clsx(team.hasNoSigner && "agent-settings__gap")}>
								{PlaybookEditService.describeCoverageCell({ names: team.signers, clientsWithNobody: team.clientsWithoutSigner, isRequired: panel.settings.requiresSignoff })}
							</span>
						</div>
					))}
				</div>
			</section>
		);
	}

	renderHistory() {
		const { panel } = this.state;
		if (panel.history.length === 0) return <div className="tk-muted">These are the defaults; nobody has saved settings for this playbook yet.</div>;
		return (
			<section className="agent-settings__section">
				<span className="tk-text-strong">Changes</span>
				<div className="tk-card">
					{panel.history.map(entry => (
						<div key={entry.id} className="agent-settings__history-row">
							<span className="agent-settings__history-who">
								{entry.changedBy} · {moment(entry.changedAt).format("MMM D, YYYY h:mm a")}
							</span>
							<span>{entry.changeNote}</span>
						</div>
					))}
				</div>
			</section>
		);
	}

	render() {
		const { panel, draft, changeNote, isLoading, loadError, isSaving, saveError, savedAt } = this.state;
		if (isLoading) return <div className="tk-empty tk-card">Loading settings…</div>;
		if (loadError) return <div className="tk-notice tk-notice--danger">{loadError}</div>;
		const isReadOnly = !panel.mayChange;
		const isDecidedByClientManagers = draft.whoDecides === PlaybookEditService.WHO_DECIDES.CLIENT_MANAGERS;
		const isMissingDecidingRole = !isDecidedByClientManagers && draft.deciderRoleIds.length === 0;
		const isMissingSignoffRole = !isDecidedByClientManagers && draft.requiresSignoff && draft.signoffRoleIds.length === 0;
		let whySaveIsOff = null;
		if (isMissingDecidingRole) whySaveIsOff = "Pick a role that decides";
		else if (isMissingSignoffRole) whySaveIsOff = "Pick a role that signs off";
		else if (!changeNote.trim()) whySaveIsOff = "Add a note first";
		const isAllTeams = draft.teamIds === null;
		const roleItems = panel.roles;
		const teamItems = panel.teams;

		return (
			<div className="agent-settings">
				{isReadOnly && (
					<div className="tk-notice">
						<div className="tk-notice__title">
							<Icon.Lock size={14} />
							Only the administrator or partner group can change these settings.
						</div>
					</div>
				)}
				<section className="agent-settings__form tk-card">
					<div className="agent-settings__field">
						<span className="tk-text-strong">Who decides on suggestions</span>
						<div className="agent-settings__select">
							<ToolsSelect
								value={draft.whoDecides}
								options={WHO_DECIDES_CHOICES}
								onChange={whoDecides => this.setDraft({ whoDecides })}
								disabled={isReadOnly}
							/>
						</div>
						{isDecidedByClientManagers ? (
							<span className="tk-muted">The person each client's profile names as Managed by decides, or its manager when nobody is chosen.</span>
						) : (
							<>
								<span className="tk-muted">Members of the client's team holding any of these roles. Any one of them can decide.</span>
								{this.renderPills({ items: roleItems, selectedIds: draft.deciderRoleIds, listName: "deciderRoleIds", disabled: isReadOnly })}
							</>
						)}
					</div>
					<div className="agent-settings__field">
						<span className="tk-text-strong">Who signs off</span>
						<ToolsCheckbox
							checked={draft.requiresSignoff}
							disabled={isReadOnly}
							onChange={() => this.setDraft({ requiresSignoff: !draft.requiresSignoff })}
							label="A run needs a sign-off before it is finished"
						/>
						{draft.requiresSignoff && isDecidedByClientManagers && (
							<span className="tk-muted">The client's senior manager signs off, or its partner when the senior manager is the one deciding.</span>
						)}
						{draft.requiresSignoff && !isDecidedByClientManagers && (
							<>
								<span className="tk-muted">Members of the client's team holding any of these roles.</span>
								{this.renderPills({ items: roleItems, selectedIds: draft.signoffRoleIds, listName: "signoffRoleIds", disabled: isReadOnly })}
							</>
						)}
						{draft.requiresSignoff && (
							<ToolsCheckbox
								checked={draft.allowSelfReview}
								disabled={isReadOnly}
								onChange={() => this.setDraft({ allowSelfReview: !draft.allowSelfReview })}
								label="Allow the person who decided to sign off too (marked as self-reviewed)"
							/>
						)}
					</div>
					<div className="agent-settings__field">
						<span className="tk-text-strong">What must be decided before sending for review</span>
						<div className="agent-settings__select">
							<ToolsSelect
								value={draft.decisionScope}
								options={SCOPE_CHOICES}
								onChange={decisionScope => this.setDraft({ decisionScope })}
								disabled={isReadOnly}
							/>
						</div>
					</div>
					<div className="agent-settings__field">
						<span className="tk-text-strong">Applies to</span>
						<ToolsCheckbox
							checked={isAllTeams}
							disabled={isReadOnly}
							onChange={() => this.setDraft({ teamIds: isAllTeams ? [] : null })}
							label="All teams"
						/>
						{!isAllTeams && this.renderPills({ items: teamItems, selectedIds: draft.teamIds || [], listName: "teamIds", disabled: isReadOnly })}
					</div>
					<div className="agent-settings__field">
						<span className="tk-text-strong">Runs by itself</span>
						<ToolsCheckbox
							checked={draft.scheduleEnabled}
							disabled={isReadOnly || !panel.canSchedule}
							onChange={() => this.setDraft({ scheduleEnabled: !draft.scheduleEnabled })}
							label="Every month, for the month just ended, on every QuickBooks client of the teams above"
						/>
						{draft.scheduleEnabled && (
							<div className="agent-settings__select">
								<ToolsSelect
									value={String(draft.scheduleDay)}
									options={DAY_CHOICES}
									onChange={day => this.setDraft({ scheduleDay: Number(day) })}
									disabled={isReadOnly}
								/>
							</div>
						)}
						{!panel.canSchedule && (
							<span className="tk-muted">Automatic runs stay off until AI prices are set up, so the firm can see what they cost.</span>
						)}
					</div>
					{!isReadOnly && (
						<div className="agent-settings__save">
							<input
								className="tk-input"
								value={changeNote}
								onChange={event => this.update({ changeNote: event.target.value })}
								placeholder="Why are these changing? Shown in the history"
								maxLength={300}
							/>
							{whySaveIsOff && <span className="tk-muted">{whySaveIsOff}</span>}
							<button
								type="button"
								className="tk-button tk-button--primary tk-button--compact"
								onClick={this.onSave}
								disabled={isSaving || !!whySaveIsOff}
								title={whySaveIsOff || undefined}
							>
								{isSaving ? "Saving…" : "Save settings"}
							</button>
						</div>
					)}
					{savedAt && (
						<div className="agent-settings__saved">
							<Icon.CheckCircle size={14} />
							Saved. New runs use these settings; earlier runs keep the settings they started with.
						</div>
					)}
					{saveError && <div className="tk-notice tk-notice--danger">{saveError}</div>}
				</section>
				{this.renderCoverage()}
				{this.renderHistory()}
			</div>
		);
	}
}

export default PlaybookSettingsPanel;
